/**
 * MondayBoard — Full replica of the Monday.com SCOPING board
 * Fetches live data via Monday.com GraphQL API and renders an interactive board
 */
import { useState, useEffect, useRef } from 'react';
import { fetchBoard, updateItemColumn, createItem } from '../mondayApi';
import apiClient from '../services/api';

// ─── Board column schema (derived from actual board structure) ───────────────
const COLUMNS = [
  { id: 'person',             title: 'Owner',        type: 'people',   width: 120 },
  { id: 'color_mm1hy3sy',     title: 'Priority',     type: 'status',   width: 110, labels: {
    3: { label: 'Critical',    color: '#333333' },
    4: { label: 'High',        color: '#401694' },
    0: { label: 'Medium',      color: '#5559df' },
    2: { label: 'Low',         color: '#579bfc' },
  }},
  { id: 'color_mkz4vq4v',     title: 'Status',       type: 'status',   width: 120, labels: {
    1: { label: 'To-Do',       color: '#00c875' },
    0: { label: 'In Progress', color: '#fdab3d' },
    5: { label: 'Done',        color: '#c4c4c4' },
    2: { label: 'Blocked',     color: '#df2f4a' },
  }},
  { id: 'color_mm1q6evg',     title: 'Track',        type: 'status',   width: 120, labels: {
    0: { label: 'Resource',    color: '#66ccff' },
    1: { label: 'Project',     color: '#9d50dd' },
    5: { label: 'Growth Task', color: '#c4c4c4' },
  }},
  { id: 'color_mm1yemtx',     title: 'Stage',        type: 'status',   width: 100, labels: {
    0: { label: 'Lead',        color: '#fdab3d' },
    1: { label: 'Active',      color: '#00c875' },
  }},
  { id: 'numeric_mm1sbnv0',   title: '% Prob',       type: 'numbers',  width: 80  },
  { id: 'numeric_mm1rg2p5',   title: 'Value',        type: 'numbers',  width: 100, format: 'currency' },
  { id: 'dropdown_mm1s5565',  title: 'Month(s)',     type: 'dropdown', width: 130, options: {
    4: 'April', 5: 'May', 6: 'June', 7: 'July', 8: 'August',
    9: 'September', 10: 'October', 11: 'November', 12: 'December',
  }},
];

// ─── Helpers ─────────────────────────────────────────────────────────────────
function parseColumnValue(cv, colDef) {
  if (!cv || !cv.value || cv.value === '{}' || cv.value === 'null') return null;
  try {
    const parsed = JSON.parse(cv.value);
    if (colDef?.type === 'status') {
      const idx = parsed.index;
      return colDef.labels?.[idx] ?? null;
    }
    if (colDef?.type === 'numbers') return cv.text ? parseFloat(cv.text) : null;
    if (colDef?.type === 'people') {
      return parsed.personsAndTeams?.map(p => p.name || `User ${p.id}`).filter(Boolean) ?? [];
    }
    if (colDef?.type === 'dropdown') {
      return parsed.ids?.map(id => colDef.options?.[id] ?? id) ?? [];
    }
  } catch { return cv.text || null; }
  return cv.text || null;
}

function fmtValue(v, col) {
  if (v === null || v === undefined) return '';
  if (col.format === 'currency') {
    const n = typeof v === 'number' ? v : parseFloat(v);
    if (isNaN(n) || n === 0) return '';
    return n >= 1000000 ? `${(n / 1000000).toFixed(1)}M kr`
         : n >= 1000    ? `${(n / 1000).toFixed(0)}K kr`
         : `${n} kr`;
  }
  if (col.type === 'numbers') {
    const n = typeof v === 'number' ? v : parseFloat(v);
    if (isNaN(n) || n === 0) return '';
    return col.title.includes('%') ? `${n}%` : String(n);
  }
  return '';
}

function getInitials(name = '') {
  const s = typeof name === 'string' ? name : String(name);
  return s.split(' ').filter(Boolean).map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

const AVATAR_PALETTE = ['#3b7ffa','#a855f7','#10c981','#f59e0b','#ef4444','#06b6d4','#ec4899','#84cc16'];
function avatarColor(name) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffffffff;
  return AVATAR_PALETTE[Math.abs(h) % AVATAR_PALETTE.length];
}

// ─── Sub-components ───────────────────────────────────────────────────────────
function StatusCell({ value }) {
  if (!value) return <td style={tdBase} />;
  return (
    <td style={tdBase}>
      <span style={{
        display: 'inline-block', padding: '3px 10px', borderRadius: 4,
        fontSize: 11, fontWeight: 600, letterSpacing: '.3px',
        background: value.color + '22', color: value.color,
        border: `1px solid ${value.color}44`,
        whiteSpace: 'nowrap',
      }}>
        {value.label}
      </span>
    </td>
  );
}

function PeopleCell({ people }) {
  if (!people || people.length === 0) return <td style={tdBase} />;
  return (
    <td style={tdBase}>
      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
        {people.slice(0, 3).map((name, i) => (
          <div key={i} title={name} style={{
            width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
            background: avatarColor(name), display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            fontSize: 10, fontWeight: 700, color: '#fff',
            border: '2px solid #060d21',
          }}>
            {getInitials(name)}
          </div>
        ))}
        {people.length > 3 && (
          <div style={{ fontSize: 11, color: '#8fa4c8' }}>+{people.length - 3}</div>
        )}
      </div>
    </td>
  );
}

function NumberCell({ value, col }) {
  const display = fmtValue(value, col);
  return (
    <span style={{ color: display ? '#dce8f8' : '#3a4f6e', fontVariantNumeric: 'tabular-nums', fontSize: 12 }}>
      {display || '—'}
    </span>
  );
}

function DropdownCell({ values }) {
  if (!values || values.length === 0) return <td style={tdBase} />;
  return (
    <td style={tdBase}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        {values.map((v, i) => (
          <span key={i} style={{
            fontSize: 10, padding: '2px 6px', borderRadius: 3,
            background: 'rgba(96,165,250,.12)', color: '#60a5fa',
            border: '1px solid rgba(96,165,250,.2)', whiteSpace: 'nowrap',
          }}>
            {v}
          </span>
        ))}
      </div>
    </td>
  );
}

function ItemRow({ item, columns, groupColor, boardId, onUpdate }) {
  const [hovered, setHovered] = useState(false);
  const [editing, setEditing] = useState(null); // colId being edited
  const [editVal, setEditVal] = useState('');

  const cvMap = {};
  item.column_values?.forEach(cv => { cvMap[cv.id] = cv; });

  function startEdit(colId, currentText) {
    setEditing(colId);
    setEditVal(currentText || '');
  }

  async function commitEdit(col) {
    if (!editing) return;
    setEditing(null);
    if (col.type === 'numbers') {
      try {
        await updateItemColumn(item.id, col.id, parseFloat(editVal) || 0);
        onUpdate();
      } catch (e) { console.error(e); }
    }
  }

  return (
    <tr
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? 'rgba(59,127,250,.04)' : 'transparent',
        transition: 'background .12s',
        borderBottom: '1px solid #0e1c38',
      }}
    >
      {/* Color stripe */}
      <td style={{ width: 4, padding: 0, background: groupColor, opacity: .5 }} />

      {/* Checkbox */}
      <td style={{ width: 32, textAlign: 'center', padding: '0 4px' }}>
        <input type="checkbox" style={{ accentColor: '#3b7ffa', cursor: 'pointer' }} />
      </td>

      {/* Name */}
      <td style={{ ...tdBase, minWidth: 200, maxWidth: 360, fontWeight: 500, color: '#dce8f8', fontSize: 13, paddingLeft: 8 }}>
        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</div>
      </td>

      {/* Dynamic columns */}
      {columns.map(col => {
        const cv = cvMap[col.id];
        const parsed = parseColumnValue(cv, col);

        if (col.type === 'status') return <StatusCell key={col.id} value={parsed} />;
        if (col.type === 'people') return <PeopleCell key={col.id} people={parsed} />;
        if (col.type === 'dropdown') return <DropdownCell key={col.id} values={parsed} />;
        if (col.type === 'numbers') {
          if (editing === col.id) {
            return (
              <td key={col.id} style={tdBase}>
                <input
                  autoFocus
                  value={editVal}
                  onChange={e => setEditVal(e.target.value)}
                  onBlur={() => commitEdit(col)}
                  onKeyDown={e => { if (e.key === 'Enter') commitEdit(col); if (e.key === 'Escape') setEditing(null); }}
                  style={{
                    width: '100%', background: '#0a1528', border: '1px solid #3b7ffa',
                    color: '#dce8f8', padding: '3px 6px', borderRadius: 3, fontSize: 12, outline: 'none',
                  }}
                />
              </td>
            );
          }
          return (
            <td key={col.id} style={{ ...tdBase, textAlign: 'right', cursor: 'text' }} onDoubleClick={() => startEdit(col.id, cv?.text)}>
              <NumberCell value={parsed} col={col} />
            </td>
          );
        }
        return <td key={col.id} style={tdBase} />;
      })}
    </tr>
  );
}

function GroupSection({ group, items, columns, boardId, onUpdate, onAddItem }) {
  const [collapsed, setCollapsed] = useState(false);
  const [addingItem, setAddingItem] = useState(false);
  const [newItemName, setNewItemName] = useState('');

  const gc = group.color || '#579bfc';

  async function handleAddItem() {
    if (!newItemName.trim()) return;
    try {
      await onAddItem(group.id, newItemName.trim());
      setNewItemName('');
      setAddingItem(false);
    } catch (e) { console.error(e); }
  }

  return (
    <tbody>
      {/* Group header */}
      <tr style={{ background: 'rgba(255,255,255,.02)' }}>
        <td style={{ width: 4, padding: 0, background: gc, borderRadius: '2px 0 0 0' }} />
        <td colSpan={columns.length + 2} style={{ padding: '6px 8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              onClick={() => setCollapsed(c => !c)}
              style={{ background: 'none', border: 'none', color: gc, cursor: 'pointer', padding: 0, fontSize: 12, lineHeight: 1 }}
            >
              {collapsed ? '▶' : '▼'}
            </button>
            <span style={{ fontWeight: 700, fontSize: 13, color: gc }}>{group.title}</span>
            <span style={{ fontSize: 11, color: '#5a7296', marginLeft: 4 }}>{items.length} item{items.length !== 1 ? 's' : ''}</span>
          </div>
        </td>
      </tr>

      {!collapsed && items.map(item => (
        <ItemRow
          key={item.id}
          item={item}
          columns={columns}
          groupColor={gc}
          boardId={boardId}
          onUpdate={onUpdate}
        />
      ))}

      {!collapsed && (
        <>
          {addingItem ? (
            <tr style={{ borderBottom: '1px solid #0e1c38' }}>
              <td style={{ width: 4, padding: 0, background: gc, opacity: .3 }} />
              <td style={{ width: 32 }} />
              <td colSpan={columns.length + 1} style={{ padding: '4px 8px' }}>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input
                    autoFocus
                    placeholder="Item name…"
                    value={newItemName}
                    onChange={e => setNewItemName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleAddItem(); if (e.key === 'Escape') setAddingItem(false); }}
                    style={{
                      flex: 1, background: '#0a1528', border: '1px solid #3b7ffa',
                      color: '#dce8f8', padding: '5px 8px', borderRadius: 4, fontSize: 12, outline: 'none',
                    }}
                  />
                  <button onClick={handleAddItem} style={{ padding: '5px 12px', background: '#3b7ffa', border: 'none', borderRadius: 4, color: '#fff', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>Add</button>
                  <button onClick={() => setAddingItem(false)} style={{ padding: '5px 10px', background: 'transparent', border: '1px solid #1c2e52', borderRadius: 4, color: '#8fa4c8', fontSize: 12, cursor: 'pointer' }}>✕</button>
                </div>
              </td>
            </tr>
          ) : (
            <tr>
              <td style={{ width: 4, padding: 0, background: gc, opacity: .2 }} />
              <td colSpan={columns.length + 2} style={{ padding: '4px 8px' }}>
                <button
                  onClick={() => setAddingItem(true)}
                  style={{ background: 'none', border: 'none', color: '#5a7296', cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4, padding: '2px 0' }}
                >
                  <span style={{ fontSize: 14, fontWeight: 700 }}>+</span> Add item
                </button>
              </td>
            </tr>
          )}
        </>
      )}
    </tbody>
  );
}

// ─── Main Board Component ────────────────────────────────────────────────────
export default function MondayBoard() {
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [hiddenCols, setHiddenCols] = useState(new Set());
  const [showColMenu, setShowColMenu] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const colMenuRef = useRef(null);

  useEffect(() => { load(); }, []);

  useEffect(() => {
    function handleClick(e) {
      if (colMenuRef.current && !colMenuRef.current.contains(e.target)) setShowColMenu(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  async function load() {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchBoard();
      setBoard(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleAddItem(groupId, name) {
    await createItem(board.id, groupId, name);
    await load();
  }

  async function handleSync() {
    setSyncing(true);
    setSyncResult(null);
    try {
      const result = await apiClient.syncMonday();
      setSyncResult({ ok: true, msg: result.message });
    } catch (err) {
      setSyncResult({ ok: false, msg: err.response?.data?.message || err.message });
    } finally {
      setSyncing(false);
      setTimeout(() => setSyncResult(null), 5000);
    }
  }

  if (loading) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 16, color: '#8fa4c8' }}>
      <div style={{ width: 32, height: 32, border: '3px solid #14213d', borderTopColor: '#3b7ffa', borderRadius: '50%', animation: 'spin .8s linear infinite' }} />
      <div style={{ fontSize: 13 }}>Loading Monday.com board…</div>
    </div>
  );

  if (error) return (
    <div style={{ padding: 32 }}>
      <div style={{ background: 'rgba(240,68,68,.1)', border: '1px solid rgba(240,68,68,.3)', borderRadius: 8, padding: 24, color: '#f87171', textAlign: 'center' }}>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>Failed to load board</div>
        <div style={{ fontSize: 13 }}>{error}</div>
        <button onClick={load} style={{ marginTop: 12, padding: '6px 16px', background: '#3b7ffa', border: 'none', borderRadius: 4, color: '#fff', cursor: 'pointer' }}>Retry</button>
      </div>
    </div>
  );

  if (!board) return null;

  // Group items by group id
  const groupedItems = {};
  board.groups.forEach(g => { groupedItems[g.id] = []; });
  board.items_page?.items?.forEach(item => {
    const gid = item.group?.id;
    if (gid && groupedItems[gid]) groupedItems[gid].push(item);
  });

  // Filter by search
  const visibleGroups = board.groups.filter(g => {
    if (!search) return true;
    const s = search.toLowerCase();
    return g.title.toLowerCase().includes(s) ||
      groupedItems[g.id]?.some(item => item.name.toLowerCase().includes(s));
  });

  const activeCols = COLUMNS.filter(c => !hiddenCols.has(c.id));

  // Summary totals
  const allItems = board.items_page?.items || [];
  const totalValue = allItems.reduce((sum, item) => {
    const cv = item.column_values?.find(c => c.id === 'numeric_mm1rg2p5');
    return sum + (parseFloat(cv?.text) || 0);
  }, 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#04091a', overflow: 'hidden' }}>

      {/* ── Board Header ── */}
      <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid #14213d', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#dce8f8', letterSpacing: '-.3px' }}>
              {board.name}
            </div>
            <div style={{ fontSize: 12, color: '#5a7296', marginTop: 2 }}>{board.description}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {/* Summary pills */}
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={pillStyle('#10c981')}>
                <span style={{ fontSize: 15, fontWeight: 700 }}>{allItems.length}</span>
                <span style={{ fontSize: 10, opacity: .7 }}>items</span>
              </div>
              <div style={pillStyle('#3b7ffa')}>
                <span style={{ fontSize: 15, fontWeight: 700 }}>
                  {totalValue >= 1000000 ? `${(totalValue / 1000000).toFixed(1)}M kr`
                   : totalValue >= 1000 ? `${(totalValue / 1000).toFixed(0)}K kr`
                   : `${totalValue} kr`}
                </span>
                <span style={{ fontSize: 10, opacity: .7 }}>pipeline</span>
              </div>
              <div style={pillStyle('#a855f7')}>
                <span style={{ fontSize: 15, fontWeight: 700 }}>{board.groups.length}</span>
                <span style={{ fontSize: 10, opacity: .7 }}>groups</span>
              </div>
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
          <div style={{ position: 'relative', flex: 1, maxWidth: 260 }}>
            <span style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: '#5a7296', fontSize: 13 }}>🔍</span>
            <input
              placeholder="Search groups or items…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                width: '100%', paddingLeft: 30, paddingRight: 10, height: 32,
                background: '#0a1528', border: '1px solid #14213d', borderRadius: 6,
                color: '#dce8f8', fontSize: 12, outline: 'none', boxSizing: 'border-box',
              }}
            />
          </div>

          <div style={{ position: 'relative' }} ref={colMenuRef}>
            <button
              onClick={() => setShowColMenu(v => !v)}
              style={toolBtn(showColMenu)}
            >
              Columns {hiddenCols.size > 0 && <span style={{ color: '#f59e0b', marginLeft: 4 }}>({hiddenCols.size} hidden)</span>}
            </button>
            {showColMenu && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, zIndex: 100, marginTop: 4,
                background: '#0a1528', border: '1px solid #14213d', borderRadius: 8,
                padding: '8px 0', minWidth: 180, boxShadow: '0 8px 32px rgba(0,0,0,.5)',
              }}>
                {COLUMNS.map(col => (
                  <label key={col.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 14px', cursor: 'pointer', fontSize: 12, color: '#dce8f8' }}>
                    <input
                      type="checkbox"
                      checked={!hiddenCols.has(col.id)}
                      onChange={() => setHiddenCols(s => {
                        const n = new Set(s);
                        n.has(col.id) ? n.delete(col.id) : n.add(col.id);
                        return n;
                      })}
                      style={{ accentColor: '#3b7ffa' }}
                    />
                    {col.title}
                  </label>
                ))}
              </div>
            )}
          </div>

          <button onClick={load} style={toolBtn(false)} title="Refresh">↻ Refresh</button>
          <button
            onClick={handleSync}
            disabled={syncing}
            style={{ ...toolBtn(false), background: syncing ? '#0a1528' : 'rgba(16,201,129,.1)', borderColor: '#10c981', color: syncing ? '#5a7296' : '#10c981' }}
          >
            {syncing ? '⟳ Syncing…' : '⇅ Sync to App'}
          </button>
          {syncResult && (
            <span style={{ fontSize: 12, color: syncResult.ok ? '#10c981' : '#f87171', fontWeight: 500 }}>
              {syncResult.ok ? '✓' : '✗'} {syncResult.msg}
            </span>
          )}
        </div>
      </div>

      {/* ── Board Table ── */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', minWidth: 900 }}>

          {/* Column widths */}
          <colgroup>
            <col style={{ width: 4 }} />
            <col style={{ width: 32 }} />
            <col style={{ width: 240 }} />
            {activeCols.map(c => <col key={c.id} style={{ width: c.width }} />)}
          </colgroup>

          {/* Sticky header */}
          <thead>
            <tr style={{ background: '#060d21', borderBottom: '2px solid #14213d', position: 'sticky', top: 0, zIndex: 10 }}>
              <th style={{ width: 4, padding: 0 }} />
              <th style={{ width: 32 }} />
              <th style={{ ...thBase, textAlign: 'left', paddingLeft: 8 }}>Name</th>
              {activeCols.map(col => (
                <th key={col.id} style={{ ...thBase, textAlign: col.type === 'numbers' ? 'right' : 'left' }}>
                  {col.title}
                </th>
              ))}
            </tr>
          </thead>

          {/* Groups */}
          {visibleGroups.map(group => {
            let items = groupedItems[group.id] || [];
            if (search) {
              const s = search.toLowerCase();
              items = items.filter(item => item.name.toLowerCase().includes(s));
              if (items.length === 0 && !group.title.toLowerCase().includes(s)) return null;
            }
            return (
              <GroupSection
                key={group.id}
                group={group}
                items={items}
                columns={activeCols}
                boardId={board.id}
                onUpdate={load}
                onAddItem={handleAddItem}
              />
            );
          })}
        </table>

        {visibleGroups.length === 0 && (
          <div style={{ textAlign: 'center', padding: 48, color: '#5a7296', fontSize: 13 }}>
            No groups match "{search}"
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Shared styles ────────────────────────────────────────────────────────────
const thBase = {
  padding: '8px 10px', fontSize: 11, fontWeight: 600,
  color: '#5a7296', textTransform: 'uppercase', letterSpacing: '.5px',
  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
};

const tdBase = {
  padding: '7px 10px', fontSize: 12, color: '#8fa4c8',
  verticalAlign: 'middle', overflow: 'hidden',
};

function pillStyle(color) {
  return {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    padding: '4px 12px', borderRadius: 6,
    background: color + '14', border: `1px solid ${color}30`,
    color,
  };
}

function toolBtn(active) {
  return {
    padding: '0 12px', height: 32, borderRadius: 6, cursor: 'pointer',
    border: `1px solid ${active ? '#3b7ffa' : '#14213d'}`,
    background: active ? 'rgba(59,127,250,.15)' : '#0a1528',
    color: active ? '#60a5fa' : '#8fa4c8',
    fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap',
  };
}
